// MarginPlan.jsx
import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/sidebar';
import Header from '../../components/header';
import { apiCall } from '../../utils/api';
import toast, { Toaster } from 'react-hot-toast';
import { FaSearch, FaEdit, FaTrash, FaPlus, FaToggleOn, FaToggleOff } from "react-icons/fa";
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

function MarginPlan() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState('dashboard');
    const [plans, setPlans] = useState([]);
    const [filteredPlans, setFilteredPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(true);

    // Modal states
    const [planModalOpen, setPlanModalOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const navigate = useNavigate();

    // Pagination states
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 10,
        totalRecords: 0,
        totalPages: 1
    });

    // Helper function to get date 7 days ago
    const getDate7DaysAgo = () => {
        const date = new Date();
        date.setDate(date.getDate() - 7);
        return date.toISOString().split('T')[0];
    };

    // Helper function to get current date
    const getCurrentDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    // Filter states with default date range
    const [filters, setFilters] = useState({
        searchValue: '',
        status: '',
        dateFrom: getDate7DaysAgo(),
        dateTo: getCurrentDate()
    });

    // Status options
    const statusOptions = [
        { id: '', name: 'All Status' },
        { id: '1', name: 'Active' },
        { id: '0', name: 'Inactive' }
    ];

    // Form state for add/edit
    const [formData, setFormData] = useState({
        name: '',
        status: '1',
        canDelete: '1'
    });

    // Fetch Plans with pagination
    const fetchPlans = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                searchValue: filters.searchValue,
                status: filters.status,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                page: page,
                limit: pagination.perPage
            };

            const result = await apiCall(
                "margin-plan",
                params,
                true
            );

            if (result.status === true) {
                setPlans(result.data || []);
                setFilteredPlans(result.data || []);
                if (result.pagination) {
                    setPagination(result.pagination);
                }
            } else {
                toast.error(result.message || "Failed to fetch plans");
                setPlans([]);
                setFilteredPlans([]);
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
            setPlans([]);
            setFilteredPlans([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle Add Plan
    const handleAddPlan = () => {
        setIsEditMode(false);
        setFormData({
            name: '',
            status: '1',
            canDelete: '1'
        });
        setSelectedPlan(null);
        setPlanModalOpen(true);
    };

    // Handle Edit Plan
    const handleEditPlan = (plan) => {
        setIsEditMode(true);
        setSelectedPlan(plan);
        setFormData({
            name: plan.name,
            status: plan.status_id || '1',
            canDelete: plan.canDelete_id || '1'
        });
        setPlanModalOpen(true);
    };

    // Handle Delete Plan
    const handleDeletePlan = (plan) => {
        Swal.fire({
            title: 'Delete Plan?',
            text: `Are you sure you want to delete "${plan.name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, Delete!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await apiCall(
                        "delete-plan",
                        {
                            plan_id: plan.plan_id
                        },
                        true
                    );

                    if (response.status === true) {
                        toast.success("Plan deleted successfully!");
                        fetchPlans(pagination.currentPage);
                    } else {
                        toast.error(response.message || "Failed to delete plan");
                    }
                } catch (error) {
                    toast.error("Network error. Please try again.");
                }
            }
        });
    };

    // Handle Status Toggle
    const handleStatusToggle = (plan) => {
        const newStatus = plan.status === 'Active' ? '0' : '1';
        const statusName = plan.status === 'Active' ? 'Inactive' : 'Active';

        Swal.fire({
            title: `Toggle Status?`,
            text: `Are you sure you want to change "${plan.name}" to ${statusName}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Toggle!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await apiCall(
                        "toggle-plan-status",
                        {
                            plan_id: plan.plan_id,
                            status: newStatus
                        },
                        true
                    );

                    if (response.status === true) {
                        toast.success(`Plan ${statusName} successfully!`);
                        fetchPlans(pagination.currentPage);
                    } else {
                        toast.error(response.message || "Failed to toggle status");
                    }
                } catch (error) {
                    toast.error("Network error. Please try again.");
                }
            }
        });
    };

    // Handle Save Plan (Add/Update)
    const handleSavePlan = async () => {
        if (!formData.name.trim()) {
            toast.error("Please enter plan name");
            return;
        }

        try {
            const endpoint = isEditMode ? "update-plan" : "add-plan";
            const params = isEditMode
                ? { plan_id: selectedPlan.plan_id, ...formData }
                : formData;

            const response = await apiCall(endpoint, params, true);

            if (response.status === true) {
                toast.success(isEditMode ? "Plan updated successfully!" : "Plan added successfully!");
                setPlanModalOpen(false);
                fetchPlans(pagination.currentPage);
            } else {
                toast.error(response.message || "Failed to save plan");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchPlans(1);
    }, []);

    // Apply filters
    const applyFilters = () => {
        fetchPlans(1);
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            searchValue: '',
            status: '',
            dateFrom: getDate7DaysAgo(),
            dateTo: getCurrentDate()
        });
        fetchPlans(1);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchPlans(newPage);
        }
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Get status color
    const getStatusColor = (status) => {
        const colors = {
            'Active': 'bg-green-100 text-green-800',
            'Inactive': 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    // Get canDelete color
    const getCanDeleteColor = (canDelete) => {
        const colors = {
            'Yes': 'bg-green-100 text-green-800',
            'No': 'bg-red-100 text-red-800'
        };
        return colors[canDelete] || 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="bg-gray-50 font-sans antialiased min-h-screen">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />

            <div className="main-content lg:ml-[72px] transition-all duration-200">
                <Header
                    currentPage={currentPage}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="p-4 sm:p-6 lg:p-8">
                    {/* Page Title */}
                    <div className="mb-6 flex justify-between items-center flex-wrap gap-2">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Margin Plans</h1>
                            <p className="text-gray-600 text-sm">View and manage all margin plans</p>
                        </div>
                        <button
                            onClick={handleAddPlan}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm flex items-center gap-2"
                        >
                            <FaPlus />
                            Add Plan
                        </button>
                    </div>

                    {/* Filter Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
                        <div className={`p-4 sm:p-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {/* Column 1: Search */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Search
                                    </label>
                                    <div className="relative">
                                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            value={filters.searchValue}
                                            onChange={(e) => handleFilterChange('searchValue', e.target.value)}
                                            placeholder="Search by plan name..."
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Column 2: Date Range */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Date Range
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="date"
                                            value={filters.dateFrom}
                                            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                            className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                        <input
                                            type="date"
                                            value={filters.dateTo}
                                            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                            className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Column 3: Status */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Status
                                    </label>
                                    <select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        {statusOptions.map(option => (
                                            <option key={option.id} value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Column 4: Actions */}
                                <div className="flex gap-2 items-end">
                                    <button
                                        onClick={applyFilters}
                                        className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
                                    >
                                        Apply
                                    </button>
                                    <button
                                        onClick={resetFilters}
                                        className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm"
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Results Count */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                        <p className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{filteredPlans.length}</span> plans
                            {pagination.totalRecords > 0 && ` of ${pagination.totalRecords}`}
                        </p>
                        {loading && (
                            <div className="flex items-center text-sm text-gray-500">
                                <svg className="animate-spin h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading...
                            </div>
                        )}
                    </div>

                    {/* Plans Table */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Plan ID
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Plan Name
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Can Delete
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredPlans.length > 0 ? (
                                        filteredPlans.map((plan) => (
                                            <tr key={plan.plan_id} className="hover:bg-gray-50 transition-colors duration-150">
                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600 cursor-pointer" onClick={() =>
                                                    navigate(`/margin/setup`, {
                                                        state: { plan_id: plan.plan_id },
                                                    })
                                                }>
                                                    #{plan.plan_id}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                    {plan.name}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(plan.status)}`}>
                                                            {plan.status}
                                                        </span>
                                                        <button
                                                            onClick={() => handleStatusToggle(plan)}
                                                            className="text-gray-500 hover:text-blue-600 transition-colors"
                                                            title="Toggle Status"
                                                        >
                                                            {plan.status === 'Active' ? (
                                                                <FaToggleOn className="text-2xl text-green-600" />
                                                            ) : (
                                                                <FaToggleOff className="text-2xl text-gray-400" />
                                                            )}
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getCanDeleteColor(plan.canDelete)}`}>
                                                        {plan.canDelete}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex flex-col">
                                                        <span className="text-sm text-gray-600">{plan.date || 'N/A'}</span>
                                                        <span className="text-xs text-gray-400">{plan.time || ''}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleEditPlan(plan)}
                                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                            title="Edit Plan"
                                                        >
                                                            <FaEdit className="text-sm" />
                                                        </button>
                                                        {plan.canDelete === 'Yes' && (
                                                            <button
                                                                onClick={() => handleDeletePlan(plan)}
                                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                title="Delete Plan"
                                                            >
                                                                <FaTrash className="text-sm" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                                    </svg>
                                                    <p className="text-sm">No plans found</p>
                                                    <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
                            <div className="text-sm text-gray-600">
                                Page {pagination.currentPage} of {pagination.totalPages}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={pagination.currentPage === 1}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                >
                                    Previous
                                </button>

                                <div className="flex gap-1">
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (pagination.currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                            pageNum = pagination.totalPages - 4 + i;
                                        } else {
                                            pageNum = pagination.currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pageNum
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={pagination.currentPage === pagination.totalPages}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pagination.totalPages
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Plan Modal */}
            {planModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setPlanModalOpen(false)}></div>
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
                            {/* Header */}
                            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">
                                        {isEditMode ? 'Edit Plan' : 'Add New Plan'}
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        {isEditMode ? `Editing "${selectedPlan?.name}"` : 'Create a new margin plan'}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setPlanModalOpen(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-6">
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Plan Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="Enter plan name..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Status
                                        </label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        >
                                            <option value="1">Active</option>
                                            <option value="0">Inactive</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Can Delete
                                        </label>
                                        <select
                                            value={formData.canDelete}
                                            onChange={(e) => setFormData({ ...formData, canDelete: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        >
                                            <option value="1">Yes</option>
                                            <option value="0">No</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-end gap-2">
                                <button
                                    onClick={() => setPlanModalOpen(false)}
                                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSavePlan}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
                                >
                                    {isEditMode ? 'Update' : 'Add'} Plan
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Toaster position="top-right" />
        </div>
    );
}

export default MarginPlan;